import { useId } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import SpacePath from "components/SpacePath";
import PrivateProviderRegistryTierInfo from "components/TierInfo/PrivateProviderRegistryTierInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { Incognito, Network } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import EmptyState from "ds/components/EmptyState";
import Link from "ds/components/Link";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useTypedContext from "hooks/useTypedContext";
import { Action } from "types/generated";
import { checkAllowedAction } from "utils/access";
import { getDocsUrl } from "utils/getDocsUrl";

import { showProviderDeleteConfirmation } from "./ProviderDeleteConfirmation";
import { showProviderDetailsDrawer } from "./ProviderDetailsDrawer";
import { showProviderDrawer } from "./ProviderDrawer";
import ProviderVersion from "./ProviderVersion";
import styles from "./styles.module.css";
import { TerraformProviderWithVersions } from "./types";
import useDeleteProvider from "./useDeleteProvider";

type ProviderOverviewProps = {
  provider: TerraformProviderWithVersions;
  loading: boolean;
};

const ProviderOverview = ({ provider, loading }: ProviderOverviewProps) => {
  const versionTitle = useId();
  const { reportSuccess, reportError } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const handleOpenUpdateProviderDrawer = () => {
    showProviderDrawer({
      provider,
    });
  };

  const [deleteProvider] = useDeleteProvider({
    refetchQueries: ["GetProviders"],
  });

  const handleDeleteProvider = () => {
    deleteProvider({ id: provider.id }, (data) => {
      if (data?.terraformProviderDelete?.deleted) {
        navigate("/providers");
        reportSuccess({
          message: `Terraform provider "${data.terraformProviderDelete.id}" was successfully deleted`,
        });
      } else {
        reportError({
          message: "Something went wrong while deleting Terraform provider, please try again.",
        });
      }
    });
  };

  const handleDeleteProviderConfirmation = () => {
    showProviderDeleteConfirmation({
      providerId: provider.id,
      onConfirm: handleDeleteProvider,
    });
  };

  const handleOpenDetailsDrawer = () => {
    showProviderDetailsDrawer({
      provider,
      onEdit: handleOpenUpdateProviderDrawer,
    });
  };

  const canUpdateProvider = checkAllowedAction(provider, Action.TerraformProviderUpdate);
  const canDeleteProvider = checkAllowedAction(provider, Action.TerraformProviderDelete);

  const isEditProviderEnabled = canUpdateProvider.result || canUpdateProvider.message;
  const isDeleteProviderEnabled = canDeleteProvider.result || canDeleteProvider.message;

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{provider.id}</ViewHeaderTitle>
          </ViewHeaderWrapper>
          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>
            {(isEditProviderEnabled || isDeleteProviderEnabled) && (
              <DropdownMenuEllipsis tooltip="Provider actions" dotsSize="medium">
                {isEditProviderEnabled && (
                  <DropdownMenuItem
                    isDisabled={!canUpdateProvider.result}
                    tooltip={canUpdateProvider.message}
                    isTooltipActive={!canUpdateProvider.result}
                    onAction={handleOpenUpdateProviderDrawer}
                  >
                    Edit
                  </DropdownMenuItem>
                )}
                {isDeleteProviderEnabled && (
                  <DropdownMenuItem
                    isDisabled={!canDeleteProvider.result}
                    tooltip={canDeleteProvider.message}
                    isTooltipActive={!canDeleteProvider.result}
                    onAction={handleDeleteProviderConfirmation}
                    danger
                  >
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuEllipsis>
            )}
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <SpacePath
              variant="meta"
              spaceId={provider.space}
              fallbackName={provider.spaceDetails.name}
            />
            <MetaInfoListItem icon={provider?.public ? Network : Incognito}>
              {provider.public ? "Public" : "Private"}
            </MetaInfoListItem>
            {provider.latestVersionNumber && (
              <MetaInfoListItem>{provider.latestVersionNumber}</MetaInfoListItem>
            )}
          </MetaInfoList>
        </ViewHeaderScrollCollapse>
      </ViewHeader>
      <PrivateProviderRegistryTierInfo type="callout" />
      <Box direction="column" grow="1" fullWidth>
        <PageInfoHeader title="Versions" titleId={versionTitle} />

        {!loading && provider.versions.length === 0 && (
          <EmptyState
            title="You have not published any versions of this provider"
            caption={
              <>
                Publishing is normally done through a CI/CD system, but you can also do it manually
                from your workstation. You can learn more about publishing Terraform providers{" "}
                <Link
                  size="small"
                  href={getDocsUrl("/vendors/terraform/provider-registry#publishing-a-provider")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </Link>
                .
              </>
            }
          />
        )}

        <Box
          direction="column"
          gap="large"
          className={styles.list}
          role="list"
          aria-labelledby={versionTitle}
        >
          {provider.versions.map((version) => (
            <ProviderVersion key={version.id} version={version} provider={provider} />
          ))}
        </Box>
      </Box>
    </>
  );
};

export default ProviderOverview;
